import React from 'react'
import { FilterGroupLayout } from '@adc/ui-core'
import { user, flows, FilterSchema } from '@adc/blueprint'
import type {
  BuyingCriteriaState,
  BuyingCriteriaLocation,
} from '@adc/blueprint'
// FlowDefinition type accessed via flows namespace
import LocationSection from '../sections/LocationSection'

const { NUDGE_GROUP_DEFINITIONS } = user.constants

export type BuyingPreferenceStepId =
  | 'location'
  | 'priceRange'
  | 'occupancy'
  | 'saleType'
  | 'propertyType'
  | 'bedsBaths'
  | 'yearBuilt'

type FlowContext = {
  filterViewModels?: FilterSchema.FilterGroupViewModelCollection
  updateFilter?: (
    groupName: string,
    key: string,
    value: FilterSchema.ValueType
  ) => void
  updateLocations?: (locations: BuyingCriteriaLocation[]) => void
  isPristine?: boolean
}

type StepComponentType = flows.FlowStep<
  BuyingPreferenceStepId,
  BuyingCriteriaState,
  FlowContext
>['component']

const isFieldEmpty = (
  prefs: BuyingCriteriaState | undefined,
  step: BuyingPreferenceStepId
): boolean => {
  if (!prefs) return true
  switch (step) {
    case 'location':
      return !prefs.locations || prefs.locations.length === 0
    case 'priceRange':
      return prefs.price?.min == null && prefs.price?.max == null
    case 'occupancy':
      return !prefs.occupancy || prefs.occupancy.length === 0
    case 'saleType':
      return !prefs.saleType || prefs.saleType.length === 0
    case 'propertyType':
      return !prefs.propertyType || prefs.propertyType.length === 0
    case 'bedsBaths':
      return prefs.beds == null && prefs.baths == null
    case 'yearBuilt':
      return prefs.yearBuilt == null
    default:
      return false
  }
}

const STEP_ORDER: BuyingPreferenceStepId[] = [
  'location',
  'priceRange',
  'occupancy',
  'saleType',
  'propertyType',
  'bedsBaths',
  'yearBuilt',
]

const getNextStep = (
  currentId: BuyingPreferenceStepId
): BuyingPreferenceStepId => {
  const idx = STEP_ORDER.indexOf(currentId)
  return STEP_ORDER[idx + 1] ?? STEP_ORDER[STEP_ORDER.length - 1]
}

const isLast = (currentId: BuyingPreferenceStepId): boolean => {
  return STEP_ORDER.indexOf(currentId) === STEP_ORDER.length - 1
}

/**
 * Filter-based step component — renders a single FilterGroupLayout
 */
const FilterStepComponent = (
  stepId: BuyingPreferenceStepId
): StepComponentType => {
  const groupDef = NUDGE_GROUP_DEFINITIONS[stepId]
  if (!groupDef) return () => null

  return ({ context, setTouched }) => {
    const groupData = context.filterViewModels?.[groupDef.name]
    if (!groupData) return null

    return (
      <div>
        <FilterGroupLayout
          elmId={`buying-pref-flow-${stepId}`}
          groupDefinition={groupDef}
          groupData={groupData}
          noPadding
          onChange={(key: string, value: FilterSchema.ValueType) => {
            context.updateFilter?.(groupDef.name, key, value)
            setTouched?.(true)
          }}
        />
      </div>
    )
  }
}

/**
 * Location step component
 */
const LocationStepComponent: StepComponentType = ({
  data,
  context,
  setTouched,
}) => (
  <LocationSection
    elmId='buying-pref-flow-location'
    autoFocus
    locations={data.locations}
    onChange={(locs: BuyingCriteriaLocation[]) => {
      context.updateLocations?.(locs)
      setTouched?.(true)
    }}
  />
)

/**
 * Buying preference flow definition for useFlow.
 *
 * Each step represents one preference question. Steps with existing data
 * are auto-skipped (canBeSkipped). The flow stops at the first empty field.
 */
export const buyingPreferenceFlowDefinition: flows.FlowDefinition<
  BuyingPreferenceStepId,
  BuyingCriteriaState,
  FlowContext
> = [
  {
    id: 'location',
    label: 'Where are you looking to buy?',
    next: () => getNextStep('location'),
    isLastStep: () => isLast('location'),
    isNextStepEnabled: ({ data }) => (data?.locations?.length ?? 0) > 0,
    canBeSkipped: ({ initialData, context }) =>
      !context?.isPristine &&
      !isFieldEmpty(initialData as BuyingCriteriaState, 'location'),
    component: LocationStepComponent,
    touched: false,
  },
  {
    id: 'priceRange',
    label: 'What price range fits your strategy?',
    next: () => getNextStep('priceRange'),
    isLastStep: () => isLast('priceRange'),
    isNextStepEnabled: ({ data }) =>
      data?.price?.min != null || data?.price?.max != null,
    canBeSkipped: ({ initialData }) =>
      !isFieldEmpty(initialData as BuyingCriteriaState, 'priceRange'),
    component: FilterStepComponent('priceRange'),
    touched: false,
  },
  {
    id: 'occupancy',
    label: 'What occupancy situation are you comfortable with?',
    next: () => getNextStep('occupancy'),
    isLastStep: () => isLast('occupancy'),
    isNextStepEnabled: ({ data }) => (data?.occupancy?.length ?? 0) > 0,
    canBeSkipped: ({ initialData }) =>
      !isFieldEmpty(initialData as BuyingCriteriaState, 'occupancy'),
    component: FilterStepComponent('occupancy'),
    touched: false,
  },
  {
    id: 'saleType',
    label: 'What types of sales are you open to?',
    next: () => getNextStep('saleType'),
    isLastStep: () => isLast('saleType'),
    isNextStepEnabled: ({ data }) => (data?.saleType?.length ?? 0) > 0,
    canBeSkipped: ({ initialData }) =>
      !isFieldEmpty(initialData as BuyingCriteriaState, 'saleType'),
    component: FilterStepComponent('saleType'),
    touched: false,
  },
  {
    id: 'propertyType',
    label: 'What types of properties interest you?',
    next: () => getNextStep('propertyType'),
    isLastStep: () => isLast('propertyType'),
    isNextStepEnabled: ({ data }) => (data?.propertyType?.length ?? 0) > 0,
    canBeSkipped: ({ initialData }) =>
      !isFieldEmpty(initialData as BuyingCriteriaState, 'propertyType'),
    component: FilterStepComponent('propertyType'),
    touched: false,
  },
  {
    id: 'bedsBaths',
    label: 'How many bedrooms and bathrooms?',
    next: () => getNextStep('bedsBaths'),
    isLastStep: () => isLast('bedsBaths'),
    isNextStepEnabled: ({ data }) => data?.beds != null || data?.baths != null,
    canBeSkipped: ({ initialData }) =>
      !isFieldEmpty(initialData as BuyingCriteriaState, 'bedsBaths'),
    component: FilterStepComponent('bedsBaths'),
    touched: false,
  },
  {
    id: 'yearBuilt',
    label: 'How old of a property would you consider?',
    next: () => getNextStep('yearBuilt'),
    isLastStep: () => true,
    isNextStepEnabled: ({ data }) => data?.yearBuilt != null,
    canBeSkipped: ({ initialData }) =>
      !isFieldEmpty(initialData as BuyingCriteriaState, 'yearBuilt'),
    component: FilterStepComponent('yearBuilt'),
    touched: false,
  },
]

/**
 * Per-step descriptions shown below the question title.
 */
export const STEP_DESCRIPTIONS: Record<BuyingPreferenceStepId, string> = {
  location:
    "Add areas you want to focus on. We'll prioritize properties in these locations.",
  priceRange: "Set property est. market value range you're targeting.",
  occupancy:
    'Vacant homes typically close faster. Occupied properties may involve tenants or extra steps.',
  saleType:
    'Each type has different timelines, competition and risk levels. Pick what matches how you buy.',
  propertyType: "Choose everything you'd seriously consider buying.",
  bedsBaths: 'Set the minimum you need.',
  yearBuilt: 'Set the minimum year built you prefer.',
}
